<template>
  <Modal
    class=""
    :visible="view"
    :scrollLock="true"
    :fullscreenMobile="true"
    :title="modalTitle"
    :gridCols="$mqAliases.xl ? 4 : 6"
    :clickOutside="true"
    :compact="true"
    @close="$emit('close')"
  >
    <section
      class="event-register m-b-36"
      :style="$mqAliases.xs || $mqAliases.sm ? 'padding-bottom: 200px' : ''"
    >
      <EventDetails class="m-t-18 m-b-18" :event="event" :compact="true" />
      <hr />

      <div class="grid m-t-18 -p-relative">
        <SvgComponent
          v-if="formLoading || saving"
          class="svg-loader"
          :icon="{
            name: 'Loading',
            color: this.Globals.colors.grayDark,
            size: 32,
          }"
        />

        <div
          v-if="!registerOK && !registerFailed && !formLoading && !saving"
          class="g-12"
        >
          <!-- Log in Section -->
          <section v-if="showLoginForm">
            <LoginForm
              :id="'event-register'"
              @on-logged-in="onLoggedIn()"
              :backgroundColor="Globals.colors.white"
            />
          </section>
          <!-- Join Loyalty Section -->
          <section v-else-if="showJoinLoyaltyButton">
            <div class="m-b-12">
              {{ insertString("Event", "LoyaltyMessage") }}
            </div>
            <ButtonComponent
              theme="main"
              :block="true"
              @click="createAccount"
              :text="insertString('Userspace', 'UpgradeToLoyalty') || 'Join'"
            />
          </section>

          <!-- Personal information -->
          <section v-else>
            <InputFieldV2
              class="m-b-12"
              v-model="userData.FirstName"
              :label="insertString('GenericForm', 'FirstNameLabel')"
              id="FirstName"
              :block="true"
              autocomplete="given-name"
              :error="error.firstName.status"
              :errorMessage="error.firstName.msg"
              @change="clearError('firstName')"
            />

            <InputFieldV2
              class="m-b-12"
              v-model="userData.LastName"
              :label="insertString('GenericForm', 'LastNameLabel')"
              id="LastName"
              :block="true"
              autocomplete="family-name"
              :error="error.lastName.status"
              :errorMessage="error.lastName.msg"
              @change="clearError('lastName')"
            />

            <div class="m-b-12">
              <label class="-main m-t-0" for="PhoneNumber">{{
                insertString("GenericForm", "PhoneLabel")
              }}</label>
              <vue-tel-input
                :class="{ '-error': error.phone.status }"
                id="PhoneNumber"
                v-model="userData.PhoneNumber"
                v-bind="vueTelProps"
                @input="clearError('phone')"
              ></vue-tel-input>
              <div v-if="error.phone.status" class="-error">
                {{ error.phone.msg }}
              </div>
            </div>

            <InputFieldV2
              class="m-b-18"
              v-model="userData.Email"
              type="email"
              :label="insertString('Global', 'EmailLabel')"
              id="Email"
              :block="true"
              autocomplete="email"
              :disabled="isLoyalty && !isInvite"
              :error="error.email.status"
              :errorMessage="error.email.msg"
              @change="clearError('email')"
            />

            <!-- Custom fields section -->
            <transition name="fade">
              <section v-if="questions" class="">
                <div v-for="(question, index) in questions.filter(q => q.IsDisabled === false)" :key="index">
                  <div v-if="question.Type === 0" class="m-b-18">
                    <p class="m-b-6">{{ question.Text }}</p>
                    <textarea
                      class="w-100"
                      v-model="answers[index]"
                      :id="`field-${index}`"
                    />
                  </div>
                  <div v-else-if="question.Type === 1" class="m-b-18">
                    <CheckBox
                      v-model="answers[index]"
                      :small="true"
                      :label="question.Text"
                      :id="`field-${index}`"
                    />
                  </div>
                  <div
                    v-else-if="question.Type === 2 && question.Answers.length"
                    class="m-b-18"
                  >
                    <p class="m-b-6">{{ question.Text }}</p>
                    <RadioButtonGroup
                      v-model="answers[index]"
                      :small="true"
                      :models="getChoiceModel(question.Answers)"
                      :groupName="`radion-${index}`"
                      :key="`key-${index}`"
                    />
                  </div>
                </div>
              </section>
            </transition>

            <div class="grid-12 m-t-36">
              <div class="g-6">
                <ButtonComponent
                  buttonType="input"
                  @click="$emit('close')"
                  :theme="'main'"
                  :ghost="true"
                  :block="true"
                  :small="false"
                  :text="insertString('Global', 'Cancel')"
                />
              </div>
              <div class="g-6">
                <ButtonComponent
                  buttonType="input"
                  @click="submit()"
                  :theme="'main'"
                  :disabled="disableSubmit"
                  :overrideThemeColor="Globals.colors.blueDark"
                  :loading="saving"
                  :block="true"
                  :small="false"
                  :text="insertString('Global', 'Submit')"
                />
              </div>
            </div>
          </section>
        </div>

        <div v-if="registerOK" class="g-12p-t-18">
          <Alert
            :type="'success'"
            :small="true"
            :text="insertString('Event', 'RegisterResponse')"
            :icon="true"
          />
          <hr />
          <div class="m-t-6" v-if="!calendarObject.sameStartAndEndTime">
            <p class="m-b-0">{{ insertString('Event', 'AddToCalendar') }}</p>
            <a rel="noopener" :href="calendarObject.googleUrl" target="_blank" class="m-r-12 -blue -clickable">{{ insertString('Event', 'Google') }}</a>
            <a rel="noopener" :href="calendarObject.appleUrl" target="_blank" class="m-r-12 -blue -clickable">{{ insertString('Event', 'Apple') }}</a>
            <a rel="noopener" :href="calendarObject.outlookUrl" target="_blank" class="-blue -clickable">{{ insertString('Event', 'Outlook') }}</a>
          </div>
        </div>
        <div v-if="registerFailed" class="g-12p-t-18">
          <div class="-bold">
            <Alert
              :type="'warning'"
              :small="true"
              :title="insertString('Event', 'RegistrationError')"
              :text="errorMessage"
              :icon="true"
            />
          </div>
        </div>
      </div>
    </section>
  </Modal>
</template>
<script>
import Api from '@/Scripts/api/api';
import siteInfo from '@/Scripts/lib/helper/siteInfo.js';
import { mapState } from 'vuex';
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';
import ButtonComponent from '@/Scripts/components/shared/ButtonV2/ButtonV2.vue';
import Modal from '@/Scripts/components/shared/Modal/ModalNew.vue';
import InputFieldV2 from '@/Scripts/components/shared/InputFieldV2/InputFieldV2.vue';
import EventDetails from '@/Scripts/components/pages/Content/Events/Assets/EventDetails.vue';
import { TRACKINGSTRINGS } from '@/Scripts/constants';
import { TrackerEventsClicked } from '@/Scripts/lib/helper/trackingHelper.js';
import 'vue-tel-input/dist/vue-tel-input.css';

const CheckBox = () => import('@/Scripts/components/shared/CheckBox/CheckBox.vue');
const LoginForm = () => import('@/Scripts/components/form/LoginForm/LoginForm.vue');
const RadioButtonGroup = () => import('@/Scripts/components/shared/RadioButtonGroup/RadioButtonGroup.vue');
const Alert = () => import(/* webpackChunkName: "Alert" */'@/Scripts/components/shared/Alert/Alert.vue');

const VueTelInput = () =>
  import(
    /* webpackChunkName: "vuetelinput" */ 'vue-tel-input'
  ).then(({ VueTelInput }) => { return VueTelInput; });
export default {
  mixins: [siteInfo],
  components: {
    Modal,
    ButtonComponent,
    InputFieldV2,
    SvgComponent,
    CheckBox,
    EventDetails,
    VueTelInput,
    LoginForm,
    RadioButtonGroup,
    Alert

  },
  props: {
    event: Object,
    view: Boolean,
    calendarObject: Object

  },
  data () {
    return {
      formLoading: false,
      debugFormData: false,
      registerOK: false,
      registerFailed: false,
      saving: false,
      errorMessage: '',
      loyaltyRestricted: true,
      answers: [],
      questions: null,
      isProUser: false,

      userData: {
        FirstName: '',
        LastName: '',
        Email: '',
        PhoneNumber: '',
        LevelGroupdId: null
      },

      error: {
        firstName: {
          id: '#FirstName',
          status: false
        },
        lastName: {
          id: '#LastName',
          status: false
        },
        phone: {
          id: '#PhoneNumber',
          status: false
        },
        email: {
          id: '#Email',
          status: false,
          msg: this.insertString('ClaimsAndRepair', 'Error', 'EmailNotValid'),
          list: {
            invite: this.insertString('Event', 'InviteOnlyError')
          }
        }
      },
      defaultErrorMessage: this.insertString('ClaimsAndRepair', 'Error', 'Default')

    };
  },
  watch: {
    view (val) {
      if (val) {
        if (!this.user.userInformation.Loyalty) {
          this.onLoggedIn();
          console.log(val, this.user);
        } else {
          this.getUserData();
        }
        this.getFormData();
      }
    }
  },

  computed: {
    ...mapState({
      isLoggedIn: state => state.appContext.isLoggedIn,
      isLoyalty: state => state.appContext.isLoyalty,
      user: state => state.userInformation
    }),
    disableSubmit () {
      return this.event && this.event.Restrictions && this.event.Restrictions[0] && this.event.Restrictions[0].LoyaltyGroupId === 2 && !this.isProUser;
    },
    lang () {
      return {
        'en-GB': 'en',
        'nb-NO': 'no',
        'sv-SE': 'sv',
        'fr-FR': 'fr',
        'de-DE': 'de'
      }[document.documentElement.lang];
    },
    vueTelProps () {
      return {
        defaultCountry: this.country,
        name: 'PhoneNumber',
        required: this.phoneRequired,
        placeholder: this.insertString('GenericForm', 'PhonePlaceholder'),
        dropdownOptions: {
          disabledDialCode: false
        },
        inputOptions: {
          showDialCode: false
        }
      };
    },
    phoneRequired () {
      // return true;
      return this.event && this.event.IsPhoneNumberRequired;
    },
    isInvite () {
      return this.event && this.event.InvitationOnly;
    },
    showLoginForm () {
      return this.loyaltyRestricted && !this.isLoggedIn && !this.isInvite;
    },
    showJoinLoyaltyButton () {
      return this.loyaltyRestricted && this.isLoggedIn && !this.isLoyalty && !this.isInvite;
    },
    modalTitle () {
      if (this.showLoginForm) {
        return this.insertString('Event', 'RegisterTitleSignUp');
      }
      if (this.showJoinLoyaltyButton) {
        return this.insertString('Event', 'RegisterTitleJoinLoyalty');
      }
      return this.insertString('Event', 'RegisterTitleDefault');
    }
  },
  methods: {
    getUserData () {
      if (this.user.userInformation.Loyalty) {
        this.userData.FirstName = this.user.userInformation.Loyalty.FirstName;
        this.userData.LastName = this.user.userInformation.Loyalty.LastName;
        this.userData.Email = this.user.userInformation.Loyalty.Email;
        this.userData.PhoneNumber = this.user.userInformation.Loyalty.PhoneNumber;
        this.userData.LevelGroupdId = this.user.userInformation.Loyalty.LevelGroupdId;
      }
    },
    getChoiceModel (obj) {
      const temp = [];
      obj.forEach((item) => {
        if (item.Text && item.Id && item.IsDisabled === false) {
          temp.push({
            label: item.Text,
            value: item.Id
          });
        }
      });
      return temp;
    },
    getFormData () {
      this.formLoading = true;
      this.registerOK = false;
      this.registerFailed = false;
      this.saving = false;

      Api.getContentByFriendlyUrl(`/api/event/form?eventId=${this.event.Id}&lang=${this.lang}`).then(
        response => {
          if (response && response.data) {
            this.loyaltyRestricted = response.data.Restrictions.length > 0;
            if (response.data.Questions) {
              this.questions = response.data.Questions.filter((q) => !q.IsDisabled);
              this.questions.forEach((q, i) => {
                this.answers[i] = q.Type === 0 ? '' : q.Type === 1 ? false : q.Type === 2 ? '' : '';
              });
            }
          }
          this.formLoaded();
        }).catch(err => {
        console.warn('Could not load form');
        this.formLoaded();
        throw err;
      });
    },
    formLoaded () {
      setTimeout(() => {
        this.formLoading = false;
      }, 330);
    },
    createAccount () {
      window.location.href = this.getLink('registrationPage');
    },
    onLoggedIn () {
      this.$store.dispatch('userInformation/getUserInformation').then(() => {
        this.$nextTick(() => {
          this.$store.dispatch('appContext/setIsLoggedIn', !!this.user.userInformation.Email);
          if (this.user.userInformation) {
            this.isProUser = this.user.userInformation.IsProUser;
            if (this.user.userInformation.Loyalty && this.user.userInformation.Loyalty.IsLoyalty) {
              this.$store.dispatch('appContext/setIsLoyalty', this.user.userInformation.Loyalty.IsLoyalty);
            }
            this.getUserData();
          }
        });
      });
    },
    validate () {
      let valid = true;
      if (!this.userData.FirstName) {
        this.setError('firstName');
        valid = false;
      }
      if (!this.userData.LastName) {
        this.setError('lastName');
        valid = false;
      }
      if (!this.userData.Email) {
        this.setError('email');
        valid = false;
      } else if (this.userData.Email && this.isInvite && !this.event.Invitations.includes(this.userData.Email)) {
        this.setError('email', 'invite');
        valid = false;
      }
      if (this.phoneRequired && !this.userData.PhoneNumber) {
        this.setError('phone');
        valid = false;
      }

      return valid;
    },
    submit () {
      if (this.validate()) {
        const data = this.collectData();
        this.registerFailed = false;
        this.registerOK = false;
        this.saving = true;
        this.errorMessage = '';
        this.trackClick(TRACKINGSTRINGS.EVENT_SUBMIT_CLICKED);
        Api.postNoLanguage(`/api/event/register?eventId=${this.event.Id}`, '', data).then(
          response => {
            this.saving = false;
            if (response.data.Success === true || response.status === 200) {
              this.registerOK = true;
            }
          }).catch(err => {
          console.warn(err.response.data.Message);
          if (err.response && err.response.data) { this.errorMessage = err.response.data.Message; }
          console.warn(err);
          this.registerFailed = true;
          this.saving = false;
          throw err;
        });
      }
    },
    trackClick (trackingString) {
      TrackerEventsClicked(trackingString, {
        id: this.event.Id,
        name: this.event.Title,
        type: this.event.EventTypeId || this.event.EventType,
        location: this.event.Location.Name || this.event.Location,
        activity: this.event.ActivityId,
        startTime: this.event.Start,
        surface: this.$mqAliases.desktop ? 'desktop' : 'mobile'
      });
    },
    collectData () {
      return {
        EventId: this.event.Id,
        Email: this.userData.Email,
        Phone: this.userData.PhoneNumber,
        Name: `${this.userData.FirstName} ${this.userData.LastName}`,
        Answers: this.collectQuestions()
      };
    },
    collectQuestions () {
      const answers = [];
      if (this.questions) {
        this.questions.forEach((q, i) => {
          if (q.Type === 0) {
            // text
            answers.push({
              EventQuestionId: q.Id,
              Text: this.answers[i]
            });
          } else if (q.Type === 1) {
            // checkbox
            answers.push({
              EventQuestionId: q.Id,
              CheckBoxResult: this.answers[i]
            });
          } else if (q.Type === 2) {
            // radio group
            answers.push({
              EventQuestionId: q.Id,
              EventQuestionAnswerId: this.answers[i]
            });
          }
        });
      }
      return answers;
    },
    setError (prop, msg = null) {
      const obj = this.error[prop];
      obj.status = true;
      if (msg !== null && typeof obj.list !== 'undefined' && typeof obj.list[msg] !== 'undefined') {
        obj.msg = obj.list[msg];
      }
      if (typeof obj.msg === 'undefined') {
        obj.msg = this.defaultErrorMessage;
      }
    },
    clearError (prop) {
      const obj = this.error[prop];
      if (obj.status) {
        obj.status = false;
      }
    }
  },
  mounted () {
    // this.onLoggedIn();
  }
};
</script>
<style lang="scss">
@import "@/scss/defaults.scss";
.event-register {
  min-height: 180px;
  .loginform-content {
    padding: 0;
  }
  .vue-tel-input {
    border: 1px solid $black !important;
    border-radius: 6px !important;
    padding: 3px !important;
    input {
      background-color: $white;
    }
  }
  textarea {
    border: 1px solid #1d1d1d;
    border-radius: 6px;
    padding: 9px 18px 12px;
    margin: 0;
  }
  .svg-loader {
    position: absolute;
    margin-top: 36px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
